// src/components/planning/EditSelectionsModal/components/PerformanceTypeSearchInput/PerformanceTypeSearchInput.tsx

import React, { useState, useEffect, useRef } from 'react';
import { Form } from 'react-bootstrap';
import { searchPerformanceTypes } from '../../services/performanceTypeService';
import styles from './PerformanceTypeSearchInput.module.css';

interface PerformanceTypeSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
}

export const PerformanceTypeSearchInput: React.FC<PerformanceTypeSearchInputProps> = ({
  value,
  onChange,
  onBlur
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [allTypes, setAllTypes] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isValidSelection, setIsValidSelection] = useState(true);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const inputRef = useRef<HTMLInputElement>(null);

  // Load all performance types on mount
  useEffect(() => {
    const loadAllTypes = async () => {
      try {
        const types = await searchPerformanceTypes('');
        setAllTypes(types);
        // Check if current value is valid
        if (value && types.length > 0) {
          const isValid = types.some(t => t.toLowerCase() === value.toLowerCase());
          setIsValidSelection(isValid);
        }
      } catch (error) {
        console.error('Error loading performance types:', error);
      }
    };
    loadAllTypes();
  }, [value]);

  // Sync input value with prop value
  useEffect(() => {
    setInputValue(value);
    if (allTypes.length > 0) {
      const isValid = !value || allTypes.some(t => t.toLowerCase() === value.toLowerCase());
      setIsValidSelection(isValid);
    }
  }, [value, allTypes]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const filterSuggestions = (searchTerm: string) => {
    if (!searchTerm) {
      setSuggestions(allTypes);
    } else {
      const filtered = allTypes.filter(type =>
        type.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setSuggestions(filtered);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsValidSelection(false); // Mark as invalid until they select from dropdown

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Filter suggestions
    timeoutRef.current = setTimeout(() => {
      filterSuggestions(newValue);
    }, 100);

    setShowSuggestions(true);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
    setIsValidSelection(true);
    onChange(suggestion); // Only update parent when valid selection made
    setShowSuggestions(false);
    inputRef.current?.blur();
  };

  const handleFocus = () => {
    setShowSuggestions(true);
    filterSuggestions(inputValue);
  };

  const handleBlur = () => {
    // Delay hiding suggestions to allow for click events
    setTimeout(() => {
      setShowSuggestions(false);

      // If the typed value doesn't match a valid type, revert to last valid value
      if (inputValue) {
        const matchedType = allTypes.find(t => t.toLowerCase() === inputValue.toLowerCase());
        if (matchedType) {
          // User typed an exact match (case-insensitive) - accept it
          setInputValue(matchedType);
          setIsValidSelection(true);
          onChange(matchedType);
        } else {
          // Invalid input - revert to original value
          setInputValue(value);
          setIsValidSelection(!!value);
        }
      } else {
        // Empty is allowed
        setIsValidSelection(true);
        onChange('');
      }

      onBlur?.();
    }, 200);
  };

  return (
    <div className={styles.container}>
      <Form.Control
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder="Select performance type..."
        className={`${styles.input} ${!isValidSelection && inputValue ? styles.invalid : ''}`}
      />
      {showSuggestions && suggestions.length > 0 && (
        <div className={styles.suggestionsContainer}>
          {suggestions.map((suggestion, index) => (
            <div
              key={`${suggestion}-${index}`}
              className={styles.suggestionItem}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent input blur
                handleSuggestionClick(suggestion);
              }}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}
      {showSuggestions && suggestions.length === 0 && inputValue && (
        <div className={styles.suggestionsContainer}>
          <div className={styles.noResults}>
            No matching performance types. Please select from the list.
          </div>
        </div>
      )}
    </div>
  );
};
